import { useMemo } from 'react';

export interface UseReportChartDataConfig<T> {
  // Source data
  data: T[];

  // Selected items to display
  displayItems: string[];

  // Comparison flags
  compareToPreviousPeriod?: boolean;
  compareToSamePeriodPreviousYear?: boolean;

  // Data accessors
  getAccessField: (item: T) => string;
  getMainDataField: (item: T, key: string) => number;
  getPreviousPeriodField?: (item: T, key: string) => number | undefined;
  getPreviousYearField?: (item: T, key: string) => number | undefined;
}

export interface ChartDataPoint {
  date: string;
  [key: string]: string | number;
}

/**
 * Custom hook to transform report data for chart visualization
 *
 * Transforms nested report data structures into flat objects suitable for Recharts:
 * - Extracts date field
 * - Flattens nested product/user group/category data
 * - Adds comparison data (previous period, previous year) with custom labels
 *
 * Example output:
 * ```
 * [
 *   {
 *     date: 'Jan 2024',
 *     'Product A': 100,
 *     'Product A (Previous Period)': 90,
 *     'Product A (Previous Year)': 80,
 *     'Product B': 150,
 *     ...
 *   }
 * ]
 * ```
 *
 * @param config - Configuration with data accessors and comparison flags
 * @returns Transformed data array ready for chart rendering
 */
export const useReportChartData = <T extends Record<string, any>>(
  config: UseReportChartDataConfig<T>
): ChartDataPoint[] => {
  const {
    data,
    displayItems,
    compareToPreviousPeriod = false,
    compareToSamePeriodPreviousYear = false,
    getAccessField,
    getMainDataField,
    getPreviousPeriodField,
    getPreviousYearField,
  } = config;

  return useMemo(() => {
    return data.map((item) => {
      const transformed: ChartDataPoint = {
        date: getAccessField(item),
      };

      displayItems.forEach((key) => {
        // Main data
        transformed[key] = getMainDataField(item, key) || 0;

        // Previous period comparison
        if (compareToPreviousPeriod && getPreviousPeriodField) {
          const previousValue = getPreviousPeriodField(item, key);

          if (previousValue !== undefined) {
            transformed[`${key}_previous_period`] = previousValue;
          }
        }

        // Previous year comparison
        if (compareToSamePeriodPreviousYear && getPreviousYearField) {
          const previousYearValue = getPreviousYearField(item, key);

          if (previousYearValue !== undefined) {
            transformed[`${key}_previous_year`] = previousYearValue;
          }
        }
      });

      return transformed;
    });
  }, [
    data,
    displayItems,
    getAccessField,
    getMainDataField,
    getPreviousPeriodField,
    getPreviousYearField,
    compareToPreviousPeriod,
    compareToSamePeriodPreviousYear,
  ]);
};

export const CHART_COLORS = [
  '#B6D72C',
  '#FF6B6B',
  '#4ECDC4',
  '#FFD93D',
  '#95E1D3',
  '#F38181',
  '#AA96DA',
  '#FCBAD3',
];

/**
 * Helper hook to get consistent color for chart lines
 *
 * @param index - Index of the item in the list
 * @returns Hex color code
 */
export const getChartColor = (index: number): string => {
  return CHART_COLORS[index % CHART_COLORS.length];
};
